<template>
  <div>
    <div class="card">
      <div class="inner">

        <header class="modal-header">
          <h1 class="title">{{ 'modals.edit_recipe' | translate }}</h1>
        </header>

        <div class="tip">
          {{ 'info.recipe_create_tip' | translate }}<br>
          {{ 'info.percent_amount_tip' | translate }}
        </div>

        <div v-if="loaded" class="workspace">

          <!-- Ingredients -->
          <div class="ingredient-section">
            <div class="flex-table">
              <div class="header">
                <div class="item">{{ 'base.ingredient' | translate }}</div>
              </div>
              <div class="body">
                <vue-scroll>
                  <draggable class="drag-area" :list="ingredients" :options="{group:'people'}">
                    <div v-for="item in ingredients" :key="item.id" class="row">
                      <div class="item">
                        <div class="i-util-bars"></div>
                        <div class="title" :title="item.name">
                          {{ shortVersion(item.name) }}</div>
                      </div>
                    </div>
                  </draggable>
                </vue-scroll>
              </div>
            </div>
          </div>

          <!-- Recipe -->
          <div class="recipe-section">
            <div class="flex-table">
              <div class="header">
                <div class="item">{{ 'base.ingredient' | translate }}</div>
                <div class="item">
                  <v-btn small icon @click="sortItems">
                    <div :style="{ transform: asc ? 'rotate(0deg)' : 'rotate(180deg)'}">↓</div>
                  </v-btn>
                  <span>%</span>
                </div>
                <div class="item">
                  <!-- <v-btn small icon @click="sortItems">↓</v-btn> -->
                  <span>{{ 'base.kg' | translate }}</span>
                </div>
              </div>
              <div class="body">
                <vue-scroll>
                  <draggable class="drag-area" @end="onChange" :list="selectedIngredients" :options="{group:'people'}">
                    <div v-for="(item, index) in selectedIngredients" :key="item.id" class="row">
                      <div class="item">
                        <div class="i-util-bars"></div>
                        <div class="title" :title="item.name">
                          {{ shortVersion(item.name) }}</div>
                      </div>
                      <div class="item">
                        <ui-number-input
                          :min="1" :max="100" postfix=" %"
                          v-model="selectedIngredients[index].percent"
                          @input="onChange"/>
                      </div>
                      <div class="item">
                        <ui-number-input
                          :min="0" :max="10000" :postfix="' ' + $t('base.kg')"
                          v-model="selectedIngredients[index].weight"
                          @input="onWeightChange"/>
                      </div>
                    </div>
                  </draggable>
                </vue-scroll>
              </div>
            </div>
          </div>
        </div>

        <template v-else>
          <v-preloader/>
        </template>

        <div class="modal-footer">
          <div v-if="selectedIngredients.length" class="inner">
            <v-btn
              :disabled="isConsultant"
              v-if="totalIngPercent === 100"
              :loading="loading"
              @click="updateRecipe">
              {{ 'base.save' | translate }}
            </v-btn>
            <div v-else class="warning">{{ 'info.percent_amount_tip' | translate }}</div>

            <div class="info">
              <div class="title">{{ 'base.percent_amount' | translate }}:</div>
              <div class="value">{{ totalIngPercent }}%</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import Draggable from 'vuedraggable'

import UiNumberInput from '@/components/ui/ui-number-input/UiNumberInput.vue'

export default {
  name: 'EditRecipeDialog',

  components: {
    UiNumberInput,
    Draggable
  },

  async mounted () {
    await this.loadIngredients()
    await this.loadRecipeDetail()
    this.calculatePercents()
    this.loaded = true

    const isSortedAsc = this.selectedIngredients.every((item, index, array) => {
      return index === 0 || array[index - 1].percent <= item.percent
    })

    this.asc = isSortedAsc
  },

  data: () => ({
    selectedIngredients: [],
    ingredients: [],
    ingredient: 0,
    totalPercent: 0,
    hasChanges: false,
    loaded: false,
    loading: false,
    asc: true,
    sorted: false
  }),

  methods: {
    ...mapActions('ingredients', [
      'selectIngredient',
      'loadIngredientsList',
      'updateIngredient'
    ]),

    ...mapActions('recipes', [
      'selectRecipe',
      'saveRecipe'
    ]),

    async loadIngredients () {
      const filters = {
        farm: this.$route.params.farm_id,
        is_animalfeed: 'false'
      }
      this.ingredients = await this.loadIngredientsList(filters)
    },

    async loadRecipeDetail () {
      this.ingredient = this.currentRecipe.ingredient
      this.currentRecipe.ingredients.forEach(element => {
        this.ingredients.forEach((item, index) => {
          if (element.ingredient === item.id) {
            let result = this.ingredients.splice(index, 1)[0]
            result.percent = element.percent
            result.weight = 0
            this.selectedIngredients.push(result)
          }
        })
      })
    },

    sortItems () {
      const isSortedAsc = this.selectedIngredients.every((item, index, array) => {
        return index === 0 || array[index - 1].percent <= item.percent
      })


      this.asc = !isSortedAsc

      this.selectedIngredients = this.selectedIngredients.sort((a, b) => {
        return this.asc
          ? a.percent - b.percent
          : b.percent - a.percent
      })
    },

    async updateRecipe () {
      const data = {
        farm: +this.$route.params.farm_id,
        id: this.currentRecipe.id,
        ingredient: this.ingredient,
        ingredients: []
      }
      let dryMatter = 0
      this.selectedIngredients.forEach((item, index) => {
        const ingredient = {
          ordering: index + 1,
          ingredient: item.id,
          percent: item.percent || 0,
          weight: 0
        }

        data.ingredients.push(ingredient)
        // dryMatter += (item.percent || 0 / 100) * item.dry_matter
        dryMatter += (item.percent || 0) / 100 * item.dry_matter
      })
      this.loading = true
      const success = await this.updateDryMetter(this.ingredient, dryMatter)
      if (!success) {
        this.loading = false
        return
      }
      this.$emit('update')
      await this.saveRecipe(data)
      this.loading = false
      this.$emit('updated')
      this.close()
    },

    async updateDryMetter (id, value) {
      let data = await this.selectIngredient(id)
      data.dry_matter = value
      let errors = await this.updateIngredient()
      if (errors) {
        this.$toasted.error(this.$t('errors.incorrect_data'))
        return false
      }
      return true
    },

    calculatePercents () {
      const COEF = 10000
      let totalPercent = 0
      this.selectedIngredients.forEach(element => {
        if (element.percent === undefined) {
          element.percent = 0
        }
        totalPercent += element.percent * COEF
      })

      // this.totalPercent = Math.floor(totalPercent / COEF * 100) / 100
      this.totalPercent = Math.round(totalPercent / COEF * 100) / 100
      const round = this.selectedIngredients.find((item) => item.percent === 0)

      // if (round === undefined && this.totalPercent !== 100 && (100 - this.totalPercent) > -1 && (100 - this.totalPercent) < 1) {
      //   this.selectedIngredients[0].percent = this.selectedIngredients[0].percent + (100 - this.totalPercent)
      // }

      if (round === undefined && Math.abs(100 - this.totalPercent) < 0.01) {
        const adjustment = 100 - this.totalPercent
        this.selectedIngredients[0].percent = parseFloat((this.selectedIngredients[0].percent + adjustment).toFixed(2))
      }
    },

    shortVersion (str) {
      return str.length > 17 ? (str.substring(0, 17) + '...') : str
    },

    onChange () {
      this.hasChanges = true
    },

    onWeightChange () {
      let totalWeight = 0
      this.selectedIngredients.forEach(item => {
        totalWeight += item.weight
      })
      this.selectedIngredients.forEach(item => {
        if (item.weight > 0) {
          item.percent = Math.round((item.weight / totalWeight) * 10000) / 100
        } else {
          item.percent = 0
        }
      })
      this.onChange()
    },

    close () {
      this.$emit('on-close')
    }
  },

  computed: {
    ...mapGetters('recipes', [
      'currentRecipe'
    ]),

    isValidPrecentAmount () {
      let amount = 0
      this.selectedIngredients.forEach(element => {
        if (element.weight === undefined) {
          element.weight = 0
        }
        amount += element.percent
      })
      return amount === 100
    },

    totalIngPercent () {
      return this.totalPercent
    }
  },

  watch: {
    hasChanges (value) {
      if (value) {
        this.calculatePercents()
        this.hasChanges = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .inner {
    position: relative;
    padding: 30px 0px;
    width: 100%;
    overflow: hidden;
  }

  .modal-header {
    margin-bottom: 20px;
    padding: 0 20px;
  }

  .header {
    height: 75px;
  }

  .tip {
    padding: 0 20px;
    margin-bottom: 25px;
    font-size: 14px;
    opacity: .6;
    line-height: 1.4;
  }

  .drag-area {
    min-height: 420px;
  }

  .drag-area .i-util-bars {
    position: absolute;
  }

  .title {
    font-size: 17px;
    font-weight: 700;
  }

  .item {
    display: flex;
    align-items: center;

    .title {
      padding-left: 35px;
    }
  }

  .flex-table::after {
    content: "";
    width: 100%;
    position: absolute;
    border-bottom: 2px solid #1da643;
  }

  .flex-table .body {
    height: 420px;
  }

  .recipe-section .item {
    padding: 1.7rem 1rem;
    width: 25%;

    &:first-child {
      width: 50%;
      position: relative;
    }
  }

  .workspace {
    display: flex;
  }

  .ingredient-section {
    width: 35%;
  }

  .recipe-section {
    width: 65%;
  }

  .row {
    cursor: pointer;
    align-items: center;
    justify-content: space-between;
  }

  .modal-footer {
    height: 68px;
    display: flex;
    align-items: center;
  }

  .modal-footer .inner {
    display: flex;
    padding: 0 35px;
    padding-top: 28px;
    align-items: center;
    justify-content: space-between;
  }

  .modal-footer .warning {
    color: #F31500;
  }

  .info {
    display: flex;
  }

  .info .title {
    font-size: 16px;
    margin-right: 25px;
  }
</style>
